import { i18n } from '../i18n.js';
/**
 * UI Turn Timer
 *
 * DISPLAY ONLY. The server owns the deadline and the auto-end (see
 * game/managers/turnTimer.js); this module never decides anything, it only
 * renders `turnDeadlineAt` / `turnRemainingMs` from the authoritative game
 * state and lets the local clock interpolate between broadcasts.
 *
 * `serverNow` in the same payload corrects for clock skew, so a client whose
 * system time is wrong still sees the real remaining time rather than a
 * countdown that ends early or never ends.
 */

let TurnTimer = {};

// Hot reloads and Jest re-requires must not leave an interval behind.
if (TurnTimer && typeof TurnTimer._dispose === 'function') {
    TurnTimer._dispose();
}

TurnTimer = (function () {
    'use strict';

    const TICK_MS = 250;
    const URGENT_MS = 30_000;
    const CRITICAL_MS = 10_000;
    // A countdown visible for the whole turn is a nag — three minutes of the
    // eye being pulled off the board to read a number that does not matter yet.
    // It earns its place only once the turn is genuinely nearly over.
    const REVEAL_MS = 15_000;
    // ...unless this turn is the one that decides the match. One strike is
    // already on the seat whose clock is running (see
    // game/managers/turnTimeoutStrikes.js), so the countdown matters from the
    // first second and stays up for the whole turn.
    const FINAL_WARNING_STRIKES = 1;

    let tickHandle = null;
    // Offset between this browser's clock and the server's, from `serverNow`.
    let clockSkewMs = 0;
    let snapshot = null;

    function t(key, fallback) {
        if (i18n && typeof i18n.t === 'function') {
            const translated = i18n.t(key);
            if (translated && translated !== key) return translated;
        }
        return fallback;
    }

    function element() {
        return document.getElementById('turn-timer');
    }

    /** @returns {HTMLElement|null} the last-chance line inside the pill */
    function warningLine(el) {
        return /** @type {HTMLElement|null} */ (el.querySelector('.turn-timer-warning'));
    }

    function formatClock(remainingMs) {
        const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }

    function serverTime() {
        return Date.now() + clockSkewMs;
    }

    /**
     * True when the seat ON THE CLOCK has already lost a turn to it, so this
     * one decides the match. Read from the authoritative view; the server owns
     * the count and the forfeit.
     */
    function onFinalTimeoutWarning(gameState) {
        const side = gameState.isYourTurn === true ? gameState.player : gameState.opponent;
        const strikes = Number(side?.turnTimeoutStrikes);
        return Number.isInteger(strikes) && strikes >= FINAL_WARNING_STRIKES;
    }

    /** Remaining ms right now, interpolated from the last authoritative packet. */
    function remainingMs() {
        if (!snapshot) return null;
        if (snapshot.paused || snapshot.deadlineAt === null) return snapshot.remainingMs;
        return Math.max(0, snapshot.deadlineAt - serverTime());
    }

    /* EVERY WRITE IS A RECONCILE (round 15, 28 September 2026). The pill is
       rendered four times a second and again on every broadcast, and the
       server publishes the same turn more than once: its turn-clock sweep
       re-announces a match the first time it sees it, 15-960 ms after the
       keep's own broadcast. Setting an attribute, a class list or a text to
       the value it already has is still a DOM write: the hidden pill was
       re-hidden (attribute and class-list records) on every tick of a
       three-minute turn, and a shown one replaced its text nodes three
       ticks in four with the same second. Each helper writes only a change,
       so a repeated state leaves the pill untouched. */
    function setHidden(node, hidden) {
        if (node.hidden !== hidden) node.hidden = hidden;
    }

    function setText(node, text) {
        if (node.textContent !== text) node.textContent = text;
    }

    function setAttr(node, name, value) {
        if (node.getAttribute(name) !== value) node.setAttribute(name, value);
    }

    function setProgress(el, progress) {
        if (progress === null) {
            if (el.style.getPropertyValue('--turn-timer-progress')) {
                el.style.removeProperty('--turn-timer-progress');
            }
            return;
        }
        if (el.style.getPropertyValue('--turn-timer-progress') !== progress) {
            el.style.setProperty('--turn-timer-progress', progress);
        }
    }

    /** `toggle(name, force)` writes only when the class really changes. */
    function setClasses(el, states) {
        for (const [name, on] of Object.entries(states)) el.classList.toggle(name, on);
    }

    function hide() {
        const el = element();
        if (!el) return;
        setHidden(el, true);
        setClasses(el, {
            'is-urgent': false,
            'is-critical': false,
            'is-paused': false,
            'is-final-warning': false,
        });
        setProgress(el, null);
        const warning = warningLine(el);
        if (warning) setHidden(warning, true);
    }

    /* THE MULLIGAN OWNS THE COUNTDOWN SEAT WHILE ITS OVERLAY IS UP.

       Both countdowns ride the SAME seat above End Turn, so one match has one
       place that means "time left". A `playing` state that arrives while the
       mulligan overlay is still up would print one pill exactly on top of the
       other (measured at 1400x640: both at (1123, 271) 179x35, 6265px^2 of
       overlap, two different numbers in one box). The overlay keeps `.active`
       through its dissolve, so this covers the fade too.

       Only the PILL stands down. The state is kept and the clock keeps
       ticking, so the pill shows on the first tick after the overlay has
       gone. Keep Hand starts the dissolve at once, and the first `playing`
       state landed inside it on every measured run (50-520 ms after the
       press, Chrome and WebKit phones). This guard used to drop that state
       and stop the clock, so the countdown knew nothing of the first turn
       until a later broadcast. When the server's second publication landed
       in the dissolve too, a player who then did nothing never saw the last
       15 seconds: 7 of 16 first turns on a private 20 s-turn server, every
       one of them a run where both states landed in the fade (16 of 16
       shown after this change).

       Presentation only: nothing here changes a decision or a deadline. The
       server still owns both clocks. */
    function mulliganCoversSeat() {
        return Boolean(document.querySelector('#mulligan-overlay.active'));
    }

    function stopTicking() {
        if (tickHandle === null) return;
        clearInterval(tickHandle);
        tickHandle = null;
    }

    function startTicking() {
        if (tickHandle !== null) return;
        tickHandle = setInterval(render, TICK_MS);
    }

    function render() {
        const el = element();
        if (!el) return;
        const remaining = remainingMs();
        if (remaining === null) {
            hide();
            stopTicking();
            return;
        }

        // The mulligan's own countdown holds the seat: the state is kept and
        // the clock keeps ticking, so the pill shows once the overlay is gone.
        if (mulliganCoversSeat()) {
            hide();
            startTicking();
            return;
        }

        // Hidden until the last stretch. Keep ticking while hidden so the
        // reveal lands on the right second rather than on the next broadcast.
        // A frozen clock cannot reach the reveal: nothing to tick for.
        if (remaining > REVEAL_MS && !snapshot.finalWarning) {
            hide();
            if (snapshot.paused) stopTicking();
            else startTicking();
            return;
        }

        setHidden(el, false);
        el.classList.toggle('is-final-warning', snapshot.finalWarning === true);
        const warning = warningLine(el);
        if (warning) {
            setHidden(warning, !snapshot.finalWarning);
            // A last-chance pill is on screen for the WHOLE turn, and its
            // sentence does not change between ticks.
            setText(warning, snapshot.finalWarning
                ? (snapshot.isYourTurn
                    ? t('game.turnTimerFinalWarningYou', 'Last chance — if this timer runs out you forfeit.')
                    : t('game.turnTimerFinalWarningOpponent', "Opponent's last chance — if this timer runs out they forfeit."))
                : '');
        }
        const value = el.querySelector('.turn-timer-value');
        if (value) setText(value, formatClock(remaining));

        const label = el.querySelector('.turn-timer-label');
        if (label) {
            setText(label, snapshot.isYourTurn
                ? t('game.yourTurn', 'Your Turn')
                : t('game.opponentTurn', "Opponent's Turn"));
        }

        // 1 → full budget, 0 → out of time. Drives the depleting bar.
        const progress = snapshot.timeoutMs > 0
            ? Math.max(0, Math.min(1, remaining / snapshot.timeoutMs))
            : 0;
        setProgress(el, String(progress));

        // While the server is resolving an action the clock is frozen, so the
        // urgency states would blink misleadingly.
        setClasses(el, {
            'is-paused': snapshot.paused === true,
            'is-urgent': !snapshot.paused && remaining <= URGENT_MS,
            'is-critical': !snapshot.paused && remaining <= CRITICAL_MS,
        });
        /* The warning line lives INSIDE the pill, and an aria-label replaces the
           accessible name of the whole element — so naming only the clock would
           hide the one sentence that says the match is on the line. */
        const spoken = [`${t('game.turnTimer', 'Turn timer')}: ${formatClock(remaining)}`];
        if (snapshot.finalWarning && warning?.textContent) spoken.push(warning.textContent);
        setAttr(el, 'aria-label', spoken.join('. '));
        const title = snapshot.paused
            ? t('game.turnTimerPaused', 'Paused while the action resolves')
            : t('game.turnTimerTitle', 'The turn ends automatically when this runs out.');
        if (el.title !== title) el.title = title;

        // A frozen clock shows its published remainder; nothing moves it
        // until the next authoritative packet.
        if (snapshot.paused) stopTicking();
        else startTicking();
    }

    /**
     * Adopt one authoritative game state. Safe to call on every broadcast.
     * @param {Object} gameState - the `gameState` half of a player view
     */
    function update(gameState) {
        // The battle surface has to still be there. Leaving a match mid-turn
        // tears the UI down while a broadcast can still be in flight, and
        // without this the countdown reappeared over the lobby a moment later
        // — caught on WebKit, where that packet lands late enough to matter.
        const onBattleSurface = document.body?.classList?.contains('game-active') === true;
        if (!onBattleSurface || !gameState || gameState.state !== 'playing') {
            snapshot = null;
            stopTicking();
            hide();
            return;
        }

        if (Number.isFinite(gameState.serverNow)) {
            clockSkewMs = gameState.serverNow - Date.now();
        }

        const timeoutMs = Number(gameState.turnTimeoutMs) || 0;
        const remaining = gameState.turnRemainingMs;
        if (timeoutMs <= 0 || !Number.isFinite(remaining)) {
            // Timer disabled server-side (TURN_TIMEOUT_MS=0) or not armed yet.
            snapshot = null;
            stopTicking();
            hide();
            return;
        }

        snapshot = {
            timeoutMs,
            remainingMs: Math.max(0, remaining),
            deadlineAt: Number.isFinite(gameState.turnDeadlineAt) ? gameState.turnDeadlineAt : null,
            // `actionInProgress` rides the SAME broadcast that starts an
            // AI/storyteller resolution; `turnTimerPaused` is the server's
            // derived flag and can arrive a packet later. Honouring both means
            // the number never ticks while the model is already thinking.
            paused: gameState.turnTimerPaused === true || gameState.actionInProgress === true,
            isYourTurn: gameState.isYourTurn === true,
            // Only the seat whose clock is actually running can lose the match
            // to it, so the other seat's strike changes nothing here.
            finalWarning: onFinalTimeoutWarning(gameState),
        };
        // render() owns the tick: it keeps the clock running while the pill
        // waits (hidden, or behind the mulligan) and stops it when frozen.
        render();
    }

    /** Leave the battle surface: drop the countdown and its interval. */
    function reset() {
        snapshot = null;
        clockSkewMs = 0;
        stopTicking();
        hide();
    }

    return {
        update,
        reset,
        _dispose: reset,
        // Test seam: the pure formatting the DOM assertions read back.
        _formatClock: formatClock,
    };
})();

export { TurnTimer };
